import { useState, useEffect } from "react";
import {
  fetchPressKitSectionsAdmin,
  fetchPressKitImages,
  fetchPressKitAssets,
  insertPressKitSection,
  updatePressKitSection,
  deletePressKitSection,
  insertPressKitImage,
  updatePressKitImage,
  deletePressKitImage,
  insertPressKitAsset,
  deletePressKitAsset,
  uploadPressFile,
  type PressKitSection as SectionRow,
  type PressKitImage as ImageRow,
  type PressKitAsset as AssetRow,
} from "../supabase";
import { Plus, Pencil, Trash2, ChevronDown, ChevronRight, Loader2, AlertTriangle } from "lucide-react";
import { ImageWithFallback } from "../figma/ImageWithFallback";
import { ImageUpload } from "./ImageUpload";
import { TranslationsPanel } from "./TranslationsPanel";
import { useAdminAuth } from "./AdminAuthContext";
import { isSupabaseConnected } from "../supabase";
import type { I18nMap } from "./TranslationsPanel";

const CATEGORIES = ["product", "lifestyle", "logo", "brand"];

type DeleteTarget = { type: "section"; id: string } | { type: "image"; id: string } | { type: "asset"; id: string } | null;

export function AdminPressKit() {
  const { hasPermission } = useAdminAuth();
  const [sections, setSections] = useState<SectionRow[]>([]);
  const [images, setImages] = useState<ImageRow[]>([]);
  const [assets, setAssets] = useState<AssetRow[]>([]);
  const [loading, setLoading] = useState(true);

  const [expandedSectionId, setExpandedSectionId] = useState<string | null>(null);
  const [editingSection, setEditingSection] = useState<SectionRow | null>(null);
  const [isNewSection, setIsNewSection] = useState(false);
  const [savingSection, setSavingSection] = useState(false);
  const [saveSectionError, setSaveSectionError] = useState<string | null>(null);

  const [editingImage, setEditingImage] = useState<ImageRow | null>(null);
  const [isNewImage, setIsNewImage] = useState(false);
  const [savingImage, setSavingImage] = useState(false);

  const [editingAsset, setEditingAsset] = useState<AssetRow | null>(null);
  const [isNewAsset, setIsNewAsset] = useState(false);
  const [savingAsset, setSavingAsset] = useState(false);
  const [assetFile, setAssetFile] = useState<File | null>(null);

  const [deleteTarget, setDeleteTarget] = useState<DeleteTarget>(null);
  const [deleting, setDeleting] = useState(false);

  const load = () => {
    if (!isSupabaseConnected()) {
      setLoading(false);
      return;
    }
    setLoading(true);
    Promise.all([
      fetchPressKitSectionsAdmin(),
      fetchPressKitImages(),
      fetchPressKitAssets(),
    ]).then(([s, i, a]) => {
      setSections(s.data ?? []);
      setImages(i.data ?? []);
      setAssets(a.data ?? []);
      setLoading(false);
    });
  };

  useEffect(() => load(), []);

  if (!hasPermission("press_kit")) {
    return (
      <p className="text-[#6F675F]" style={{ fontSize: "14px" }}>
        You don&apos;t have access to this section.
      </p>
    );
  }

  const handleSaveSection = async () => {
    if (!editingSection) return;
    setSaveSectionError(null);
    setSavingSection(true);
    try {
      if (isNewSection) {
        const { error } = await insertPressKitSection({
          slug: editingSection.slug,
          title: editingSection.title,
          content: editingSection.content ?? undefined,
          position: editingSection.position ?? 0,
          visible: editingSection.visible ?? true,
          i18n: (editingSection.i18n as Record<string, Record<string, string>>) ?? undefined,
        });
        if (error) {
          setSaveSectionError(error.message);
          return;
        }
      } else {
        const { error } = await updatePressKitSection(editingSection.id, {
          slug: editingSection.slug,
          title: editingSection.title,
          content: editingSection.content ?? null,
          position: editingSection.position ?? 0,
          visible: editingSection.visible ?? true,
          i18n: editingSection.i18n ?? null,
        });
        if (error) {
          setSaveSectionError(error.message);
          return;
        }
      }
      setEditingSection(null);
      setIsNewSection(false);
      setExpandedSectionId(null);
      load();
    } finally {
      setSavingSection(false);
    }
  };

  const handleSaveImage = async () => {
    if (!editingImage) return;
    setSavingImage(true);
    try {
      const url = editingImage.image_url;
      const hasDataUrl = isNewImage && url && url.startsWith("data:");
      const finalUrl = hasDataUrl ? url : (editingImage.image_url || "");
      if (isNewImage && !finalUrl) {
        setSavingImage(false);
        return;
      }
      if (isNewImage) {
        await insertPressKitImage({
          section_slug: editingImage.section_slug || null,
          image_url: finalUrl,
          alt_text: editingImage.alt_text || null,
          category: editingImage.category || null,
          position: editingImage.position ?? 0,
        });
      } else {
        await updatePressKitImage(editingImage.id, {
          section_slug: editingImage.section_slug ?? null,
          image_url: editingImage.image_url,
          alt_text: editingImage.alt_text ?? null,
          category: editingImage.category ?? null,
          position: editingImage.position ?? 0,
        });
      }
      setEditingImage(null);
      setIsNewImage(false);
      load();
    } finally {
      setSavingImage(false);
    }
  };

  const handleSaveAsset = async () => {
    if (!editingAsset) return;
    setSavingAsset(true);
    try {
      let fileUrl = editingAsset.file_url;
      if (assetFile) {
        const { url, error } = await uploadPressFile(assetFile, "downloads");
        if (error || !url) {
          setSavingAsset(false);
          return;
        }
        fileUrl = url;
      }
      if (isNewAsset && !fileUrl) {
        setSavingAsset(false);
        return;
      }
      await insertPressKitAsset({
        name: editingAsset.name,
        file_url: fileUrl,
        type: editingAsset.type || null,
      });
      setEditingAsset(null);
      setIsNewAsset(false);
      setAssetFile(null);
      load();
    } finally {
      setSavingAsset(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      if (deleteTarget.type === "section") await deletePressKitSection(deleteTarget.id);
      else if (deleteTarget.type === "image") await deletePressKitImage(deleteTarget.id);
      else if (deleteTarget.type === "asset") await deletePressKitAsset(deleteTarget.id);
      setDeleteTarget(null);
      load();
    } finally {
      setDeleting(false);
    }
  };

  const sectionForm = (sec: SectionRow, isNew: boolean) => (
    <div className="space-y-4 p-5 bg-[#F7F5F1] rounded-b-xl border-t border-[#E6E0D7]">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className="block text-[#6F675F] tracking-[0.12em] uppercase mb-2" style={{ fontSize: "10px" }}>Slug *</label>
          <input
            value={sec.slug}
            onChange={(e) => setEditingSection({ ...sec, slug: e.target.value })}
            className="w-full px-3 py-2.5 bg-white border border-[#E6E0D7] rounded-lg"
            style={{ fontSize: "13px" }}
            placeholder="brand-overview"
          />
        </div>
        <div>
          <label className="block text-[#6F675F] tracking-[0.12em] uppercase mb-2" style={{ fontSize: "10px" }}>Title (EN) *</label>
          <input
            value={sec.title}
            onChange={(e) => setEditingSection({ ...sec, title: e.target.value })}
            className="w-full px-3 py-2.5 bg-white border border-[#E6E0D7] rounded-lg"
            style={{ fontSize: "13px" }}
          />
        </div>
      </div>
      <div>
        <label className="block text-[#6F675F] tracking-[0.12em] uppercase mb-2" style={{ fontSize: "10px" }}>Position</label>
        <input
          type="number"
          value={sec.position ?? 0}
          onChange={(e) => setEditingSection({ ...sec, position: parseInt(e.target.value, 10) || 0 })}
          className="w-full px-3 py-2.5 bg-white border border-[#E6E0D7] rounded-lg"
          style={{ fontSize: "13px" }}
        />
      </div>
      <div>
        <label className="block text-[#6F675F] tracking-[0.12em] uppercase mb-2" style={{ fontSize: "10px" }}>Content (EN)</label>
        <textarea
          rows={6}
          value={sec.content ?? ""}
          onChange={(e) => setEditingSection({ ...sec, content: e.target.value })}
          className="w-full px-3 py-2.5 bg-white border border-[#E6E0D7] rounded-lg resize-y"
          style={{ fontSize: "13px" }}
          placeholder="Markdown: **bold**, [link](url)"
        />
      </div>
      <TranslationsPanel
        fields={[
          { key: "title", label: "Title", englishValue: sec.title },
          { key: "content", label: "Content", type: "textarea", rows: 6, englishValue: sec.content ?? "" },
        ]}
        i18n={(sec.i18n as I18nMap) ?? {}}
        onChange={(i18n) => setEditingSection({ ...sec, i18n })}
      />
      <label className="flex items-center gap-3 cursor-pointer">
        <input
          type="checkbox"
          checked={sec.visible ?? true}
          onChange={(e) => setEditingSection({ ...sec, visible: e.target.checked })}
          className="w-4 h-4 rounded border-[#E6E0D7] text-[#6F7F63]"
        />
        <span className="text-[#2D2A26]" style={{ fontSize: "13px" }}>Visible on press page</span>
      </label>
      <div className="flex flex-wrap items-center gap-3 pt-2">
        {saveSectionError && <p className="text-[#8B5C58] text-sm">{saveSectionError}</p>}
        <button
          onClick={() => { setEditingSection(null); setIsNewSection(false); setExpandedSectionId(null); }}
          disabled={savingSection}
          className="px-5 py-2.5 border border-[#E6E0D7] text-[#6F675F] rounded-lg hover:bg-white disabled:opacity-50"
          style={{ fontSize: "12px" }}
        >
          Cancel
        </button>
        <button
          onClick={handleSaveSection}
          disabled={savingSection}
          className="px-5 py-2.5 bg-[#6F7F63] text-white rounded-lg hover:bg-[#5F6F53] disabled:opacity-70 flex items-center gap-2"
          style={{ fontSize: "12px" }}
        >
          {savingSection && <Loader2 size={14} className="animate-spin" />}
          {isNew ? "Create" : "Save"}
        </button>
      </div>
    </div>
  );

  return (
    <div className="max-w-4xl">
      <h1
        className="text-[#2D2A26] tracking-[0.08em] mb-8"
        style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "28px", fontWeight: 400 }}
      >
        Press Kit
      </h1>

      {loading && (
        <p className="text-[#6F675F] mb-6" style={{ fontSize: "13px" }}>Loading…</p>
      )}

      <div className="space-y-12">
        {/* ─── Sections ─── */}
        <section>
          <h2 className="text-[#2D2A26] mb-4" style={{ fontSize: "18px", fontWeight: 500 }}>Sections</h2>
          <p className="text-[#6F675F] mb-4" style={{ fontSize: "13px" }}>
            Edit title and content in English; use the Translations panel for 日本語 (JP), Nederlands (NL), and Français (FR). The public press page shows the language chosen by the visitor.
          </p>

          {isNewSection && editingSection && (
            <div className="bg-white rounded-xl shadow-[0_2px_16px_rgba(40,32,20,0.04)] overflow-hidden mb-4">
              <div className="px-5 py-4 border-b border-[#E6E0D7] flex items-center justify-between">
                <span className="text-[#6F675F]" style={{ fontSize: "14px" }}>New section</span>
              </div>
              {sectionForm(editingSection, true)}
            </div>
          )}

          {!isNewSection && (
            <button
              type="button"
              onClick={() => {
                setIsNewSection(true);
                setEditingSection({
                  id: "",
                  slug: "",
                  title: "",
                  content: "",
                  position: sections.length,
                  visible: true,
                  created_at: "",
                  updated_at: "",
                  i18n: {},
                });
              }}
              className="flex items-center gap-2 px-5 py-2.5 bg-[#6F7F63] text-white rounded-lg hover:bg-[#5F6F53] transition-colors tracking-[0.1em] uppercase mb-4"
              style={{ fontSize: "11px" }}
            >
              <Plus size={16} strokeWidth={1.5} />
              Add Section
            </button>
          )}

          <div className="space-y-3">
            {sections.map((sec) => (
              <div key={sec.id} className="bg-white rounded-xl shadow-[0_2px_16px_rgba(40,32,20,0.04)] overflow-hidden">
                <div
                  className="px-5 py-4 flex items-center justify-between cursor-pointer hover:bg-[#F7F5F1] transition-colors"
                  onClick={() => {
                    if (expandedSectionId === sec.id) {
                      setExpandedSectionId(null);
                      setEditingSection(null);
                    } else {
                      setExpandedSectionId(sec.id);
                      setEditingSection({ ...sec });
                    }
                  }}
                >
                  <div className="flex items-center gap-3">
                    {expandedSectionId === sec.id ? (
                      <ChevronDown size={18} className="text-[#6F675F] shrink-0" />
                    ) : (
                      <ChevronRight size={18} className="text-[#6F675F] shrink-0" />
                    )}
                    <div>
                      <h3 className="text-[#2D2A26]" style={{ fontSize: "16px" }}>{sec.title}</h3>
                      <p className="text-[#D8D1C7]" style={{ fontSize: "11px" }}>{sec.slug} · Position {sec.position}</p>
                    </div>
                    <span
                      className="px-2.5 py-1 rounded-full shrink-0"
                      style={{
                        background: sec.visible ? "#74856D15" : "#D8D1C715",
                        color: sec.visible ? "#74856D" : "#D8D1C7",
                        fontSize: "10px",
                        letterSpacing: "0.1em",
                        textTransform: "uppercase",
                      }}
                    >
                      {sec.visible ? "Visible" : "Hidden"}
                    </span>
                  </div>
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); setDeleteTarget({ type: "section", id: sec.id }); }}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-[#6F675F] hover:text-[#8B5C58] border border-[#E6E0D7] rounded-lg transition-colors shrink-0"
                    style={{ fontSize: "11px" }}
                  >
                    <Trash2 size={12} /> Delete
                  </button>
                </div>
                {expandedSectionId === sec.id && editingSection?.id === sec.id && sectionForm(editingSection, false)}
              </div>
            ))}
          </div>
        </section>

        {/* ─── Images ─── */}
        <section>
          <h2 className="text-[#2D2A26] mb-4" style={{ fontSize: "18px", fontWeight: 500 }}>Images</h2>

          {isNewImage && editingImage && (
            <div className="bg-[#F7F5F1] rounded-xl p-5 mb-4 border border-[#E6E0D7]">
              <p className="text-[#6F675F] mb-4" style={{ fontSize: "12px" }}>New image</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <ImageUpload label="Image" value={editingImage.image_url} onChange={(url) => setEditingImage({ ...editingImage, image_url: url })} previewAspect="aspect-video" />
                <div className="space-y-3">
                  <div>
                    <label className="block text-[#6F675F] tracking-[0.12em] uppercase mb-1" style={{ fontSize: "10px" }}>Section</label>
                    <select value={editingImage.section_slug ?? ""} onChange={(e) => setEditingImage({ ...editingImage, section_slug: e.target.value || null })} className="w-full px-3 py-2.5 bg-white border border-[#E6E0D7] rounded-lg" style={{ fontSize: "13px" }}>
                      <option value="">—</option>
                      {sections.map((s) => <option key={s.id} value={s.slug}>{s.slug}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-[#6F675F] tracking-[0.12em] uppercase mb-1" style={{ fontSize: "10px" }}>Category</label>
                    <select value={editingImage.category ?? ""} onChange={(e) => setEditingImage({ ...editingImage, category: e.target.value || null })} className="w-full px-3 py-2.5 bg-white border border-[#E6E0D7] rounded-lg" style={{ fontSize: "13px" }}>
                      <option value="">—</option>
                      {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-[#6F675F] tracking-[0.12em] uppercase mb-1" style={{ fontSize: "10px" }}>Alt text</label>
                    <input value={editingImage.alt_text ?? ""} onChange={(e) => setEditingImage({ ...editingImage, alt_text: e.target.value || null })} className="w-full px-3 py-2.5 bg-white border border-[#E6E0D7] rounded-lg" style={{ fontSize: "13px" }} />
                  </div>
                  <div className="flex gap-2">
                    <button onClick={() => { setEditingImage(null); setIsNewImage(false); }} className="px-4 py-2 border border-[#E6E0D7] text-[#6F675F] rounded-lg text-sm">Cancel</button>
                    <button onClick={handleSaveImage} disabled={savingImage || !editingImage.image_url} className="px-4 py-2 bg-[#6F7F63] text-white rounded-lg text-sm disabled:opacity-70 flex items-center gap-2">
                      {savingImage && <Loader2 size={14} className="animate-spin" />} Add
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {!isNewImage && (
            <button
              type="button"
              onClick={() => {
                setIsNewImage(true);
                setEditingImage({
                  id: "",
                  section_slug: null,
                  image_url: "",
                  alt_text: null,
                  category: null,
                  position: images.length,
                  created_at: "",
                });
              }}
              className="flex items-center gap-2 px-5 py-2.5 bg-[#6F7F63] text-white rounded-lg hover:bg-[#5F6F53] transition-colors tracking-[0.1em] uppercase mb-4"
              style={{ fontSize: "11px" }}
            >
              <Plus size={16} strokeWidth={1.5} />
              Add Image
            </button>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {images.map((img) => (
              <div key={img.id} className="bg-white rounded-xl overflow-hidden shadow-[0_2px_16px_rgba(40,32,20,0.04)]">
                <div className="aspect-[4/3] bg-[#F2EEE8]">
                  <ImageWithFallback src={img.image_url} alt={img.alt_text || "Press"} className="w-full h-full object-cover" />
                </div>
                <div className="p-3 flex justify-between items-center">
                  <span className="text-[#6F675F]" style={{ fontSize: "11px" }}>{img.category || "—"}</span>
                  <div className="flex gap-1">
                    <button
                      onClick={() => { setEditingImage({ ...img }); setIsNewImage(false); }}
                      className="p-1.5 text-[#6F675F] hover:text-[#6F7F63]"
                      title="Edit"
                    >
                      <Pencil size={12} />
                    </button>
                    <button onClick={() => setDeleteTarget({ type: "image", id: img.id })} className="p-1.5 text-[#6F675F] hover:text-[#8B5C58]" title="Delete">
                      <Trash2 size={12} />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {editingImage && !isNewImage && editingImage.id && (
            <div className="bg-[#F7F5F1] rounded-xl p-5 mt-4 border border-[#E6E0D7]">
              <p className="text-[#6F675F] mb-4" style={{ fontSize: "12px" }}>Edit image</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <ImageUpload label="Image" value={editingImage.image_url} onChange={(url) => setEditingImage({ ...editingImage, image_url: url })} previewAspect="aspect-video" />
                <div className="space-y-3">
                  <div>
                    <label className="block text-[#6F675F] tracking-[0.12em] uppercase mb-1" style={{ fontSize: "10px" }}>Section</label>
                    <select value={editingImage.section_slug ?? ""} onChange={(e) => setEditingImage({ ...editingImage, section_slug: e.target.value || null })} className="w-full px-3 py-2.5 bg-white border border-[#E6E0D7] rounded-lg" style={{ fontSize: "13px" }}>
                      <option value="">—</option>
                      {sections.map((s) => <option key={s.id} value={s.slug}>{s.slug}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-[#6F675F] tracking-[0.12em] uppercase mb-1" style={{ fontSize: "10px" }}>Category</label>
                    <select value={editingImage.category ?? ""} onChange={(e) => setEditingImage({ ...editingImage, category: e.target.value || null })} className="w-full px-3 py-2.5 bg-white border border-[#E6E0D7] rounded-lg" style={{ fontSize: "13px" }}>
                      <option value="">—</option>
                      {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-[#6F675F] tracking-[0.12em] uppercase mb-1" style={{ fontSize: "10px" }}>Alt text</label>
                    <input value={editingImage.alt_text ?? ""} onChange={(e) => setEditingImage({ ...editingImage, alt_text: e.target.value || null })} className="w-full px-3 py-2.5 bg-white border border-[#E6E0D7] rounded-lg" style={{ fontSize: "13px" }} />
                  </div>
                  <div className="flex gap-2">
                    <button onClick={() => setEditingImage(null)} className="px-4 py-2 border border-[#E6E0D7] text-[#6F675F] rounded-lg text-sm">Cancel</button>
                    <button onClick={handleSaveImage} disabled={savingImage} className="px-4 py-2 bg-[#6F7F63] text-white rounded-lg text-sm disabled:opacity-70 flex items-center gap-2">
                      {savingImage && <Loader2 size={14} className="animate-spin" />} Save
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </section>

        {/* ─── Assets (Downloads) ─── */}
        <section>
          <h2 className="text-[#2D2A26] mb-4" style={{ fontSize: "18px", fontWeight: 500 }}>Downloads</h2>

          {isNewAsset && editingAsset && (
            <div className="bg-[#F7F5F1] rounded-xl p-5 mb-4 border border-[#E6E0D7]">
              <p className="text-[#6F675F] mb-4" style={{ fontSize: "12px" }}>New download</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-[#6F675F] tracking-[0.12em] uppercase mb-2" style={{ fontSize: "10px" }}>Display name *</label>
                  <input value={editingAsset.name} onChange={(e) => setEditingAsset({ ...editingAsset, name: e.target.value })} className="w-full px-3 py-2.5 bg-white border border-[#E6E0D7] rounded-lg" style={{ fontSize: "13px" }} placeholder="e.g. MOECHA Logo Black" />
                </div>
                <div>
                  <label className="block text-[#6F675F] tracking-[0.12em] uppercase mb-2" style={{ fontSize: "10px" }}>File *</label>
                  <input type="file" onChange={(e) => setAssetFile(e.target.files?.[0] ?? null)} className="w-full px-3 py-2.5 bg-white border border-[#E6E0D7] rounded-lg" style={{ fontSize: "13px" }} />
                  {assetFile && <p className="mt-1 text-[#6F675F]" style={{ fontSize: "12px" }}>{assetFile.name}</p>}
                </div>
                <div>
                  <label className="block text-[#6F675F] tracking-[0.12em] uppercase mb-2" style={{ fontSize: "10px" }}>Type (optional)</label>
                  <input value={editingAsset.type ?? ""} onChange={(e) => setEditingAsset({ ...editingAsset, type: e.target.value || null })} className="w-full px-3 py-2.5 bg-white border border-[#E6E0D7] rounded-lg" style={{ fontSize: "13px" }} placeholder="e.g. logo_black, pdf" />
                </div>
                <div className="flex items-end gap-2">
                  <button onClick={() => { setEditingAsset(null); setIsNewAsset(false); setAssetFile(null); }} className="px-4 py-2.5 border border-[#E6E0D7] text-[#6F675F] rounded-lg text-sm">Cancel</button>
                  <button onClick={handleSaveAsset} disabled={savingAsset || !editingAsset.name.trim() || !assetFile} className="px-4 py-2.5 bg-[#6F7F63] text-white rounded-lg text-sm disabled:opacity-70 flex items-center gap-2">
                    {savingAsset && <Loader2 size={14} className="animate-spin" />} Add
                  </button>
                </div>
              </div>
            </div>
          )}

          {!isNewAsset && (
            <button
              type="button"
              onClick={() => {
                setIsNewAsset(true);
                setAssetFile(null);
                setEditingAsset({ id: "", name: "", file_url: "", type: null, created_at: "" });
              }}
              className="flex items-center gap-2 px-5 py-2.5 bg-[#6F7F63] text-white rounded-lg hover:bg-[#5F6F53] transition-colors tracking-[0.1em] uppercase mb-4"
              style={{ fontSize: "11px" }}
            >
              <Plus size={16} strokeWidth={1.5} />
              Add Download
            </button>
          )}

          <div className="space-y-2">
            {assets.map((asset) => (
              <div key={asset.id} className="bg-white rounded-xl overflow-hidden shadow-[0_2px_16px_rgba(40,32,20,0.04)] p-4 flex items-center justify-between">
                <div>
                  <p className="text-[#2D2A26]" style={{ fontSize: "14px" }}>{asset.name}</p>
                  <a href={asset.file_url} target="_blank" rel="noopener noreferrer" className="text-[#6F7F63] hover:underline" style={{ fontSize: "12px" }}>{asset.file_url}</a>
                </div>
                <button onClick={() => setDeleteTarget({ type: "asset", id: asset.id })} className="flex items-center gap-1.5 px-3 py-1.5 text-[#6F675F] hover:text-[#8B5C58] border border-[#E6E0D7] rounded-lg" style={{ fontSize: "11px" }}>
                  <Trash2 size={12} /> Delete
                </button>
              </div>
            ))}
          </div>
        </section>
      </div>

      {/* Single delete confirmation */}
      {deleteTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 backdrop-blur-sm px-4">
          <div className="bg-white rounded-xl p-6 max-w-sm w-full shadow-2xl">
            <div className="flex items-center gap-3 mb-4">
              <AlertTriangle size={20} className="text-[#8B5C58] shrink-0" />
              <h3 className="text-[#2D2A26]" style={{ fontSize: "16px" }}>
                {deleteTarget.type === "section" && "Delete this section?"}
                {deleteTarget.type === "image" && "Delete this image?"}
                {deleteTarget.type === "asset" && "Delete this download?"}
              </h3>
            </div>
            <div className="flex gap-3">
              <button onClick={() => setDeleteTarget(null)} disabled={deleting} className="flex-1 py-2.5 border border-[#E6E0D7] text-[#6F675F] rounded-lg hover:bg-[#F2EEE8] disabled:opacity-50" style={{ fontSize: "12px" }}>Cancel</button>
              <button onClick={handleDelete} disabled={deleting} className="flex-1 py-2.5 bg-[#8B5C58] text-white rounded-lg hover:bg-[#7A4B48] disabled:opacity-70 flex items-center justify-center gap-2" style={{ fontSize: "12px" }}>
                {deleting && <Loader2 size={14} className="animate-spin" />}
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
